import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { Gl07ExportConfiguration, InvoiceReferenceKind, Sie4ExportConfiguration } from '../../model/company';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { createDimensionSelection, DimensionType, DimensionTypeToString } from '../../../accounting/model/const/enum';

@Component({
    selector: 'app-company-gl07-config',
    templateUrl: './company-gl07-config.component.html',
    styleUrls: ['./company-gl07-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanyGl07ConfigComponent implements OnInit {
  constructor(private _cf: FormControlExFactory) {}
  @Input() dataContext: Gl07ExportConfiguration;
  private _form: FormGroup;
  @Input() set form(value: AbstractControl) {
    this._form = value as FormGroup;
  }
  get form(): FormGroup {
    return this._form;
  }
  @Input() dimensionSelection: SelectionItem[] = createDimensionSelection(undefined, true);
  @Input() customerOrSupplierIdDimSelection: SelectionItem[] = [
    new SelectionItem(DimensionTypeToString(DimensionType.FixedValue), DimensionType.FixedValue),
    new SelectionItem(DimensionTypeToString(DimensionType.CounterPart), DimensionType.CounterPart),
  ];

  encodingSelection: SelectionItem[] = [
    new SelectionItem('UTF-8', 'UTF-8'),
    new SelectionItem('CP-1252', 'CP-1252'),
    new SelectionItem('UTF-8 BOM', 'UTF-8 BOM'),
    new SelectionItem('CP-437', 'CP-437'),
  ];
  invoiceReferenceSelection: SelectionItem[] = [
    new SelectionItem('CONST.NONE', InvoiceReferenceKind.None),
    new SelectionItem('INVOICE.INVOICE_NUMBER', InvoiceReferenceKind.InvoiceNumber),
    new SelectionItem('INVOICE.OCRNUMBER', InvoiceReferenceKind.Ocr),
  ];
  ngOnInit(): void {
    if (this.form.contains('dim1')) return;

    this.form.addControl('fileExtension', this._cf.create('COMMON.FILE_EXTENSION', this.dataContext.fileExtension));
    this.form.addControl('dim1', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_1', this.dataContext.dim1));
    this.form.addControl('dim2', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_2', this.dataContext.dim2));
    this.form.addControl('dim3', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_3', this.dataContext.dim3));
    this.form.addControl('dim4', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_4', this.dataContext.dim4));
    this.form.addControl('dim5', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_5', this.dataContext.dim5));
    this.form.addControl('dim6', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_6', this.dataContext.dim6));
    this.form.addControl('dim7', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_7', this.dataContext.dim7));
    this.form.addControl('d1Fixed', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_1', this.dataContext.d1Fixed));
    this.form.addControl('d2Fixed', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_2', this.dataContext.d2Fixed));
    this.form.addControl('d3Fixed', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_3', this.dataContext.d3Fixed));
    this.form.addControl('d4Fixed', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_4', this.dataContext.d4Fixed));
    this.form.addControl('d5Fixed', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_5', this.dataContext.d5Fixed));
    this.form.addControl('d6Fixed', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_6', this.dataContext.d6Fixed));
    this.form.addControl('d7Fixed', this._cf.create('ACCOUNTING_EXPORT.DIMENSION_7', this.dataContext.d7Fixed));
    this.form.addControl(
      'invoiceReference',
      this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.INVOICE_REFERENCE', this.dataContext.invoiceReference)
    );
    this.form.addControl(
      'asymmetricBundling',
      this._cf.create(
        'COMPANY.SETTINGS_GL07_FIELDS.ASYMMETRIC_BUNDLING',
        this.dataContext.asymmetricBundling,
        null,
        null,
        'COMPANY.SETTINGS_GL07_FIELDS.HELP_TEXT.ASYMMETRIC_BUNDLING'
      )
    );
    this.form.addControl('bundleFile', this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.BUNDLE_FILE', this.dataContext.bundleFile));
    this.form.addControl('zeroFillAmounts', this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.ZERO_FILL_AMOUNTS', this.dataContext.zeroFillAmounts));
    this.form.addControl('interface', this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.INTERFACE', this.dataContext.interface));
    this.form.addControl('ledgerType', this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.LEDGER_TYPE', this.dataContext.ledgerType));
    this.form.addControl('taxCode', this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.TAX_CODE', this.dataContext.taxCode));
    this.form.addControl(
      'customerOrSupplierIdDim',
      this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.CUSTOMERORSUPPLIERID', this.dataContext.customerOrSupplierIdDim)
    );
    this.form.addControl(
      'customerOrSupplierIdDimFixed',
      this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.CUSTOMERORSUPPLIERID', this.dataContext.customerOrSupplierIdDimFixed)
    );
    this.form.addControl('encodingName', this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.ENCODING', this.dataContext.encodingName));
    this.form.addControl(
      'voucherType',
      this._cf.create(
        'COMPANY.SETTINGS_GL07_FIELDS.VOUCHER_TYPE',
        this.dataContext.voucherType,
        null,
        null,
        'COMPANY.SETTINGS_GL07_FIELDS.HELP_TEXT.VOUCHER_TYPE'
      )
    );
    this.form.addControl(
      'includeTodaysWork',
      this._cf.create(
        'COMPANY.SETTINGS_GL07_FIELDS.INCLUDE_TODAYS_WORK',
        this.dataContext.includeTodaysWork,
        null,
        null,
        'COMPANY.SETTINGS_GL07_FIELDS.HELP_TEXT.INCLUDE_TODAYS_WORK'
      )
    );
    this.form.addControl('client', this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.CLIENT', this.dataContext.client));
  }
}
